import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import {
  listClients,
  listClientPage,
  createClient,
  updateClient,
  deleteClient,
  getClientAuditLog,
} from "./api";
import type {
  ClientPageQuery,
  CreateClientPayload,
  UpdateClientPayload,
} from "./types";

const QUERY_KEY = ["clients"];

export function useClients(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => listClients(100, 0),
    enabled: options.enabled ?? true,
  });
}

function normalizeClientPageQuery(query: ClientPageQuery): ClientPageQuery {
  return {
    limit: query.limit,
    offset: query.offset,
    search: query.search.trim(),
  };
}

export function useClientPage(query: ClientPageQuery, enabled = true) {
  const normalizedQuery = normalizeClientPageQuery(query);
  const lastSuccessfulPage = useRef<
    Awaited<ReturnType<typeof listClientPage>> | undefined
  >(undefined);

  const result = useQuery({
    queryKey: [...QUERY_KEY, "page", normalizedQuery],
    queryFn: ({ signal }) => listClientPage(normalizedQuery, signal),
    enabled,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (result.isSuccess && !result.isPlaceholderData) {
      lastSuccessfulPage.current = result.data;
    }
  }, [result.data, result.isPlaceholderData, result.isSuccess]);

  if (result.isError && !result.data && lastSuccessfulPage.current) {
    return {
      ...result,
      data: lastSuccessfulPage.current,
      isPlaceholderData: true,
    };
  }

  return result;
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateClientPayload) => createClient(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateClientPayload;
    }) => updateClient(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteClient(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useClientAuditLog(id: string) {
  return useQuery({
    queryKey: ["client_audit", id],
    queryFn: () => getClientAuditLog(id, 100, 0),
    enabled: !!id,
  });
}
